import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { parseError } from 'utils/parseTypedErrorMessage';

import { getGroupGuid } from 'slices/sliceHelpers';

// Store the pending poll timer ID outside Redux state (timers can't be serialized)
let activePollTimeout = null;
// Generation counter so in-flight poll responses from a stopped interval are discarded instead of dispatching.
// Without it, a straggler request resolving after stopPollDChat() replaces `dchat` with a fresh object — new
// `pending_confirmation` array identity — which used to wipe the user's first Accept/Reject in the approval modal.
let pollGeneration = 0;

// Delay between one poll settling and the next one starting.
const POLL_DELAY_MS = 500;
// Matches the gateway's 30s cutoff so a hung request fails the same way in every environment.
const POLL_REQUEST_TIMEOUT_MS = 30000;

// Slice
const dchatSlice = createSlice({
  name: 'dchat',
  initialState: {
    current_sitrep_id: null,
    dchat: {},
    dchatId: null,
    status: 'idle',
    error: null,
    streamtext: '', // New state variable for websocket stream text
    isFetchGroupAIUsageLoaded: true,
    groupAIUsage: {},
    isFetchAllGroupsAIUsageLoaded: true,
    allGroupsAIUsage: [],
  },
  reducers: {
    setCurrentSitrepId: (state, action) => {
      state.current_sitrep_id = action.payload;
    },
    pollDChatSuccess: (state, action) => {
      state.dchat = action.payload;
    },
    postDChatSuccess: (state, action) => {
      state.dchatId = action.payload;
      // Nothing since polling is used to fetch data
    },
    postCancelRunSuccess: (state, action) => {
      state.dchat = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setDchatStream: (state, action) => {
      state.streamtext = action.payload; // Update stream text
    },
    setPostDchatInit: (state) => {
      state.streamtext = ''; // Update stream text
    },
    setSelectedDChat: (state, action) => {
      const chat = action.payload || {};
      state.dchat = chat; // Set selected chat
      // Transient run state (status/streamtext) lives at the slice root, not inside dchat — reset it on every switch so
      // a new/different chat never inherits the prior session's spinner or status (the "zombie" new chat). Reopening a
      // paused chat re-derives the right UI from its persisted status below.
      state.streamtext = '';
      if (
        chat.status === 'Planning' ||
        chat.status === 'Gathering Data' ||
        chat.status === 'Compacting' ||
        chat.status === 'Executing Tools'
      ) {
        state.status = 'ongoing';
      } else {
        state.status = 'idle';
      }
    },
    setFetchGroupAIUsageLoaded: (state, action) => {
      state.isFetchGroupAIUsageLoaded = action.payload;
    },
    fetchGroupAIUsageSuccess: (state, action) => {
      const newGroupAIUsage = { ...state.groupAIUsage };
      newGroupAIUsage[action.payload.group_guid] = action.payload.usage;
      state.groupAIUsage = newGroupAIUsage;
    },
    setFetchAllGroupsAIUsageLoaded: (state, action) => {
      state.isFetchAllGroupsAIUsageLoaded = action.payload;
    },
    fetchAllGroupsAIUsageSuccess: (state, action) => {
      state.allGroupsAIUsage = action.payload;
    },
  },
});

export const {
  setCurrentSitrepId,
  pollDChatSuccess,
  postDChatSuccess,
  postCancelRunSuccess,
  setError,
  setStatus,
  setDchatStream, // New action for setting stream text
  setPostDchatInit,
  setSelectedDChat,
  setFetchGroupAIUsageLoaded,
  fetchGroupAIUsageSuccess,
  fetchGroupAIUsageError,
  setFetchAllGroupsAIUsageLoaded,
  fetchAllGroupsAIUsageSuccess,
} = dchatSlice.actions;

export default dchatSlice.reducer;

// Function to stop active polling
export const stopPollDChat = () => {
  pollGeneration += 1; // invalidate any in-flight poll responses
  if (activePollTimeout) {
    clearTimeout(activePollTimeout);
    activePollTimeout = null;
  }
};

export const pollDChat = (dchatId) => {
  return (dispatch, getState) => {
    // Clear any existing polling timer before starting a new one
    stopPollDChat();

    dispatch(setStatus('loading'));
    const state = getState();
    const current_sitrep_id = state.dchat.current_sitrep_id;
    const group_guid = getGroupGuid(getState);

    const data = {
      id: dchatId,
      dchat_id: dchatId,
      current_sitrep_id: current_sitrep_id,
      group_guid: group_guid,
    };

    // Initialize start time and a flag to track if the toast has been shown
    const startTime = Date.now();
    let toastShown = false;
    const gen = pollGeneration; // responses from this poll loop are valid only while the generation is current

    // Each poll is scheduled only after the previous one settles, so a slow response never stacks up requests.
    const scheduleNextPoll = () => {
      activePollTimeout = setTimeout(poll, POLL_DELAY_MS);
    };

    const poll = () => {
      activePollTimeout = null;
      // Check if 2 minutes have passed and the toast hasn't been shown yet
      if (!toastShown && Date.now() - startTime > 120000) {
        // 120000 ms = 2 minutes
        toast.info(
          'Large datasets take time to process. Try selecting fewer files for a faster response.'
        );
        toastShown = true; // Prevent the toast from showing multiple times
      }

      axios
        .get(`${config.apiGateway.disasterchat}/poll`, {
          params: data,
          timeout: POLL_REQUEST_TIMEOUT_MS,
        })
        .then((response) => {
          if (gen !== pollGeneration) return; // stale in-flight response after stopPollDChat — discard
          const dchat = response.data;
          if (!dchat) {
            dispatch(setStatus('succeeded'));
            stopPollDChat();
          } else if (dchat.status === 'Error') {
            toast.error(dchat?.err_msg);
            dispatch(setError('DChat Error. Cancel run and try again.'));
            dispatch(cancelRun(dchat));
            stopPollDChat();
          } else if (dchat.status === 'Complete') {
            dispatch(pollDChatSuccess(dchat));
            dispatch(setStatus('succeeded'));
            stopPollDChat();
          } else if (dchat.status === 'PendingConfirmation') {
            // Agent paused on a write-tool confirmation (HITL). Stop polling;
            // the confirmation modal drives the next transition via resumeDChat.
            dispatch(pollDChatSuccess(dchat));
            dispatch(setStatus('pending_confirmation'));
            stopPollDChat();
          } else {
            dispatch(setStatus('ongoing'));
            dispatch(pollDChatSuccess(dchat));
            if (gen === pollGeneration) scheduleNextPoll();
          }
        })
        .catch((error) => {
          if (gen !== pollGeneration) return; // stale in-flight response after stopPollDChat — discard
          console.error('Error checking DChat status:', error);
          dispatch(setError(error.message));
          dispatch(setStatus('failed'));
          toast.error(
            parseError(error) ??
              'Pratus error: please contact support@disastertech.com'
          );
          stopPollDChat();
        });
    };

    scheduleNextPoll();
  };
};

export const postDChat = (data) => async (dispatch, getState) => {
  dispatch(setStatus('loading'));
  dispatch(setPostDchatInit());
  try {
    const state = getState();
    const current_sitrep_id = state.dchat.current_sitrep_id;
    const group_guid = getGroupGuid(getState);
    const payload = {
      ...data,
      dchat_id: data.dchatId,
      current_sitrep_id,
      group_guid: group_guid,
    };
    delete payload.dchatId;
    let response;
    if (data.dchatId) {
      response = await axios.put(`${config.apiGateway.disasterchat}`, payload);
    } else {
      response = await axios.post(`${config.apiGateway.disasterchat}`, payload);
    }
    dispatch(postDChatSuccess(response.data));
    // Begin polling for dchat data. pollDChat sets status to 'loading' then
    // 'ongoing'; do not flash 'succeeded' here or the input UI briefly swaps to
    // the completed/text-input layout before polling restarts.
    dispatch(pollDChat(response.data));
  } catch (error) {
    toast.error(parseError(error) ?? 'Failed to send disaster chat message');
    dispatch(setError(error.message));
    dispatch(setStatus('failed'));
  }
};

/**
 * Resume an agent session paused on a write-tool confirmation. `decisions` is
 * one entry per pending confirmation, in order: { decision: 'approve' | 'edit'
 *
 * | 'reject', tool_name?, edited_args?, message? }. The resumed run executes
 *
 * Server-side; we go back to polling for the outcome (which may be another
 * PendingConfirmation). Resolves `true` on success and `false` on failure so
 * the caller (ReviewQueueModal) can leave its "applying" spinner on failure.
 */
export const resumeDChat = (dchatId, decisions) => {
  return async (dispatch, getState) => {
    dispatch(setStatus('loading'));
    try {
      const group_guid = getGroupGuid(getState);
      await axios.post(`${config.apiGateway.disasterchat}/resume`, {
        dchat_id: dchatId,
        group_guid,
        decisions,
      });
      dispatch(pollDChat(dchatId));
      return true;
    } catch (error) {
      toast.error(parseError(error) ?? 'Failed to resume disaster chat');
      dispatch(setError(error.message));
      dispatch(setStatus('failed'));
      return false;
    }
  };
};

/**
 * "Compact now": summarize the chat's earlier turns into the model's context
 * (PRAT-4843). COMS runs it synchronously and returns the updated chat, which
 * carries the new divider marker and context size. Resolves true on success, or
 * the (also toasted) reason on failure so the caller can show it in place.
 */
export const compactDChat = (dchatId) => async (dispatch, getState) => {
  try {
    const group_guid = getGroupGuid(getState);
    const response = await axios.post(
      `${config.apiGateway.disasterchat}/compact`,
      { dchat_id: dchatId, group_guid }
    );
    // The owner may have switched chats while the compaction ran.
    if (getState().dchat.dchat?.id === dchatId) {
      dispatch(pollDChatSuccess(response.data));
    }
    return true;
  } catch (error) {
    const reason = parseError(error) ?? 'Failed to compact the chat';
    toast.error(reason);
    return reason;
  }
};

export const cancelRun = (dchat) => async (dispatch) => {
  dispatch(setStatus('loading'));
  try {
    const response = await axios.post(
      `${config.apiGateway.disasterchat}/cancel`,
      {
        dchat_id: dchat.id,
        current_sitrep_id: dchat.current_sitrep_id,
      }
    );
    dispatch(postCancelRunSuccess(response.data));
    dispatch(setStatus('succeeded'));
    // Begin polling for dchat data
    dispatch(pollDChat(response.data.id));
  } catch (error) {
    toast.error(parseError(error) ?? 'Failed to cancel disaster chat');
    dispatch(setError(error.message));
    dispatch(setStatus('failed'));
  }
};

export const fetchGroupAIUsage =
  ({ group_guid }) =>
  async (dispatch) => {
    dispatch(setFetchGroupAIUsageLoaded(false));

    try {
      const response = await axios(config.apiGateway.fetchGroupAIUsage, {
        method: 'POST',
        data: JSON.stringify({ group_guid }),
      });
      dispatch(fetchGroupAIUsageSuccess({ group_guid, usage: response.data }));
      dispatch(setFetchGroupAIUsageLoaded(true));
    } catch (error) {
      console.error('Error with DGPT task:', error);
      dispatch(setFetchGroupAIUsageLoaded(true));
    }
  };

export const fetchAllGroupsAIUsage = () => async (dispatch) => {
  dispatch(setFetchAllGroupsAIUsageLoaded(false));

  try {
    const response = await axios(config.apiGateway.fetchAllGroupsAIUsage, {
      method: 'POST',
    });

    const data = response.data || [];
    dispatch(fetchAllGroupsAIUsageSuccess(data));
    dispatch(setFetchAllGroupsAIUsageLoaded(true));
  } catch (error) {
    console.error('Error withq DGPT task:', error);
    dispatch(setFetchAllGroupsAIUsageLoaded(true));
  }
};
